import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EquipmentDetailsModal = ({ equipment, onClose }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      operational: 'text-success',
      maintenance: 'text-warning',
      'out-of-service': 'text-error',
      retired: 'text-muted-foreground'
    };
    return colors?.[status] || 'text-foreground';
  };

  const getConditionColor = (condition) => {
    const colors = {
      excellent: 'text-success',
      good: 'text-primary',
      fair: 'text-warning',
      poor: 'text-error'
    };
    return colors?.[condition] || 'text-foreground';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={(e) => e?.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{equipment?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{equipment?.serialNumber}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Equipment Image */}
          <div className="w-full h-64 rounded-lg overflow-hidden bg-muted">
            <Image
              src={equipment?.image}
              alt={equipment?.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Status & Condition */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Status</p>
              <p className={`text-lg font-semibold capitalize ${getStatusColor(equipment?.status)}`}>
                {equipment?.status?.replace('-', ' ')}
              </p>
            </div>
            <div className="bg-muted/30 rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1">Condition</p>
              <p className={`text-lg font-semibold capitalize ${getConditionColor(equipment?.condition)}`}>
                {equipment?.condition}
              </p>
            </div>
          </div>

          {/* Equipment Details */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Info" size={20} />
              Equipment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Icon name="Tag" size={18} className="text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Category</p>
                  <p className="text-sm font-medium text-foreground">{equipment?.category}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="Package" size={18} className="text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="text-sm font-medium text-foreground">{equipment?.type}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="MapPin" size={18} className="text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="text-sm font-medium text-foreground">{equipment?.location}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="Building" size={18} className="text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Manufacturer</p>
                  <p className="text-sm font-medium text-foreground">{equipment?.manufacturer}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="Box" size={18} className="text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Model</p>
                  <p className="text-sm font-medium text-foreground">{equipment?.model}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="DollarSign" size={18} className="text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Purchase Cost</p>
                  <p className="text-sm font-medium text-foreground">{formatCurrency(equipment?.cost)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Maintenance Information */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Wrench" size={20} />
              Maintenance Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Last Maintenance</p>
                <p className="text-sm font-medium text-foreground">{formatDate(equipment?.lastMaintenance)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Next Maintenance</p>
                <p className="text-sm font-medium text-foreground">{formatDate(equipment?.nextMaintenance)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Total Services</p>
                <p className="text-sm font-medium text-foreground">{equipment?.maintenanceCount} times</p>
              </div>
            </div>
          </div>

          {/* Warranty & Purchase */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Shield" size={20} />
              Warranty & Purchase
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Purchase Date</p>
                <p className="text-sm font-medium text-foreground">{formatDate(equipment?.purchaseDate)}</p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Warranty Expiry</p>
                <p className="text-sm font-medium text-foreground">{formatDate(equipment?.warrantyExpiry)}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button variant="primary" iconName="Wrench" iconPosition="left">
              Log Maintenance
            </Button>
            <Button variant="outline" iconName="AlertTriangle" iconPosition="left">
              Report Issue
            </Button>
            <Button variant="outline" iconName="Edit" iconPosition="left">
              Edit Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetailsModal;